// frontend/src/components/Common/RouteErrorBoundary.jsx
// 路由層 Error Boundary：攔截 lazy chunk 下載失敗或路由頁面 render 錯誤，避免白屏

import { Component } from 'react';
import { Box, Typography, Button, Stack } from '@mui/material';

// 判斷是否為 chunk 下載失敗（webpack/CRA 的 ChunkLoadError，或等價的 CSS chunk 失敗），
// 這類錯誤通常是版本更新後舊 chunk 網址失效，整頁重新整理即可恢復
function isChunkLoadError(error) {
  if (!error) return false;
  if (error.name === 'ChunkLoadError') return true;
  if (error.code === 'CSS_CHUNK_LOAD_FAILED') return true;
  return /Loading (CSS )?chunk .+ failed/i.test(error.message || '');
}

class RouteErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    console.error('[RouteErrorBoundary]', error, errorInfo);
  }

  handleRetry = () => {
    // React.lazy 一旦 reject 會永久記為 Rejected，單純重設 state 不會重新下載 chunk，
    // 因此重試一律走整頁重新整理，確保真的重新發出 chunk 請求
    window.location.reload();
  };

  handleGoHome = () => {
    // 整頁導航（非 SPA router）：chunk 的 Rejected 態與程式錯誤的 state 都需要整頁重置才能恢復
    window.location.assign('/');
  };

  render() {
    if (this.state.hasError) {
      const isChunkError = isChunkLoadError(this.state.error);
      const title = isChunkError ? '頁面載入失敗，請重試' : '頁面發生錯誤';
      const description = isChunkError
        ? '可能是網路問題或版本更新導致資源下載失敗'
        : '這個頁面發生了程式問題，重新整理可能無法解決；您可以先回首頁繼續使用其他功能';
      const primaryAction = isChunkError
        ? { label: '重新整理', onClick: this.handleRetry }
        : { label: '回首頁', onClick: this.handleGoHome };
      const secondaryAction = isChunkError
        ? { label: '回首頁', onClick: this.handleGoHome }
        : { label: '重新整理', onClick: this.handleRetry };

      return (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: '50vh',
            p: 3,
            textAlign: 'center',
          }}
        >
          <Typography variant="h6" color="error" sx={{ mb: 1 }}>
            {title}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {description}
          </Typography>
          <Stack direction="row" spacing={2}>
            <Button variant="contained" onClick={primaryAction.onClick}>
              {primaryAction.label}
            </Button>
            <Button variant="outlined" onClick={secondaryAction.onClick}>
              {secondaryAction.label}
            </Button>
          </Stack>
        </Box>
      );
    }
    return this.props.children;
  }
}

export default RouteErrorBoundary;
